import type { ApiBody, ApiResponse } from '../../api/contract';
import { paths } from '../../routes/paths';

export type ApprovalDetail = ApiResponse<'/api/v1/approval-requests/{id}', 'get'>['data'];
export type ApprovalSummary = ApiResponse<'/api/v1/approval-requests', 'get'>['data'][number];
export type FinancialApprovalAction = ApprovalDetail['actionType'];
export type FinancialApprovalStatus = ApprovalDetail['status'];

export type ApprovalDecisionInput = ApiBody<'/api/v1/approval-requests/{id}/decision', 'post'>;

/**
 * The real `/execute` endpoint has no request body (see openapi/backoffice-api.yaml) — unlike wallet
 * adjustments, there is no client-supplied preview hash to negotiate. This type is intentionally empty;
 * execute() still accepts it (per the brief's repository contract) for symmetry with decide().
 */
export type ApprovalExecutionInput = Record<string, never>;

export interface ApprovalListFilters {
  readonly partnerId: string;
  readonly actionType: FinancialApprovalAction | '';
  readonly status: FinancialApprovalStatus | '';
  readonly entityType: string;
  readonly entityId: string;
}

export interface ApprovalListQuery {
  readonly cursor: string;
  readonly pageSize: number;
  readonly filters: ApprovalListFilters;
}

export const financialApprovalActions: readonly FinancialApprovalAction[] = [
  'WALLET_ADJUST',
  'WITHDRAWAL_COMPLETE',
  'WITHDRAWAL_RELEASE',
  'PARTNER_CREDIT_PUBLISH',
];

export const financialApprovalStatuses: readonly FinancialApprovalStatus[] = [
  'PENDING',
  'APPROVED',
  'REJECTED',
  'EXPIRED',
  'EXECUTED',
];

/**
 * Deep-links to the entity a decided/executed approval governs. Derived only from fields already on
 * `FinancialApproval` (actionType, entityId, partnerId) — no additional backend calls are made. Returns
 * null when the action type is unmapped (the spec's `FinancialApprovalAction` enum has no commission
 * action today, so a "mock Commission adjustment" deep link is not derivable from this endpoint).
 */
export function targetEntityPath(
  approval: Pick<ApprovalDetail, 'actionType' | 'entityId' | 'partnerId'>,
): string | null {
  switch (approval.actionType) {
    case 'WALLET_ADJUST':
      return approval.entityId === null
        ? null
        : `${paths.affiliateWallets}/${encodeURIComponent(approval.entityId)}`;
    case 'WITHDRAWAL_COMPLETE':
    case 'WITHDRAWAL_RELEASE':
      return approval.entityId === null
        ? null
        : `${paths.affiliateWithdrawals}/${encodeURIComponent(approval.entityId)}`;
    case 'PARTNER_CREDIT_PUBLISH':
      return `${paths.partnerCredit}/${encodeURIComponent(approval.partnerId)}`;
    default:
      return null;
  }
}
